import { LeadActivity } from '@spike/client-model';
import Button from 'components/UI/V3/Button/Button';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { Table } from 'components/UI/V3/Table/Table';
import { FunctionComponent } from 'react';
import { useMediaQuery } from 'react-responsive';
import LeadActivityRow from './LeadActivityRow';
import LeadActivityRowSkeleton from './LeadActivityRowSkeleton';
import styles from './LeadActivityTable.module.scss';

const COLUMN_COUNT = 4;

interface LeadActivityTableProps {
    activities: Array<LeadActivity>;
    timeZone: string;
    isLoading: boolean;
    hasError: boolean;
    page: number;
    size: number;
    totalPages: number;
    onPageChange: (page: number) => void;
    onSizeChange: (size: number) => void;
    onRetry: () => void;
}

const LeadActivityTable: FunctionComponent<LeadActivityTableProps> = props => {
    const isMobile = useMediaQuery({ query: '(max-width:768px)' });

    const renderTableContent = () => {
        if (props.isLoading) {
            const skeletonRowCount = props.activities.length > 0 ? props.activities.length : props.size;

            return (
                <TableBody>
                    {Array.from({ length: skeletonRowCount }, (_, index) => (
                        <LeadActivityRowSkeleton key={index} />
                    ))}
                </TableBody>
            );
        }

        if (props.hasError) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={COLUMN_COUNT}
                            className={styles.stateCell}
                        >
                            <p className={styles.stateTitle}>We couldn’t load this activity</p>
                            <p className={styles.stateText}>Something went wrong. Please try again.</p>
                            <Button
                                label="Try again"
                                variant="outlined"
                                size="small"
                                onClick={props.onRetry}
                            />
                        </td>
                    </tr>
                </TableBody>
            );
        }

        if (props.activities.length === 0) {
            return (
                <TableBody>
                    <tr>
                        <td
                            colSpan={COLUMN_COUNT}
                            className={styles.stateCell}
                        >
                            <p className={styles.stateTitle}>No activity recorded yet</p>
                            <p className={styles.stateText}>Activity for this lead will appear here as it happens.</p>
                        </td>
                    </tr>
                </TableBody>
            );
        }

        return (
            <TableBody>
                {props.activities.map(activity => (
                    <LeadActivityRow
                        key={activity.id}
                        activity={activity}
                        timeZone={props.timeZone}
                    />
                ))}
            </TableBody>
        );
    };

    return (
        <Table
            className={styles.table}
            firstColumnFixed={isMobile}
            pagination={{ page: props.page, totalPages: props.totalPages, size: props.size }}
            onPageChange={props.onPageChange}
            onSizeChange={props.onSizeChange}
        >
            <TableHeader>
                <TableHeaderCell>Date &amp; Time</TableHeaderCell>
                <TableHeaderCell>Activity</TableHeaderCell>
                <TableHeaderCell>Details</TableHeaderCell>
                <TableHeaderCell>Performed By</TableHeaderCell>
            </TableHeader>
            {renderTableContent()}
        </Table>
    );
};

export default LeadActivityTable;
